import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "antd";
import {
  CustomerServiceOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import AutoContactStrip from "./AutoContactStrip";
import useSiteProfile from "../../hooks/useSiteProfile";
import paths from "../../routes/paths";
import { brandName } from "../../pages/care/careData";
import { OLIVEEB_COLORS } from "../../workspaces/colors";
import "../layout/layout.css";
import { websiteOwnerTypeForWorkspace } from "pages/websiteUtils";
import { readable } from "../../workspaces/workspaceUtils";
// import { websiteOwnerTypeForWorkspace } from "pages/websiteUtils";

const cleanText = (value) => (typeof value === "string" ? value.trim() : "");
const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";
const normalizeRoleToken = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const workspaceDisplayName = (workspace) =>
  cleanText(workspace?.name) ||
  cleanText(workspace?.displayName) ||
  cleanText(workspace?.organizationName) ||
  cleanText(workspace?.businessName) ||
  "Current workspace";

const workspaceDisplayType = (workspace) =>
  readable(
    workspace?.facilityType ||
      workspace?.careFacilityType ||
      workspace?.organizationType ||
      workspace?.accountType ||
      workspace?.product,
    "Workspace",
  );

const userDisplayName = (user) =>
  cleanText(user?.displayName) ||
  [
    user?.preferredFirstName || user?.firstName,
    user?.preferredLastName || user?.lastName,
  ]
    .map(cleanText)
    .filter(Boolean)
    .join(" ") ||
  cleanText(user?.name) ||
  cleanText(user?.username) ||
  cleanText(user?.email) ||
  "Dealer User";

const userRoleLabel = (user) =>
  cleanText(user?.roleLabel) ||
  cleanText(user?.profession) ||
  cleanText(user?.jobTitle) ||
  cleanText(user?.title) ||
  cleanText(user?.primaryRole) ||
  cleanText(user?.role) ||
  "Dealer User";

const userInitials = (name) => {
  const parts = cleanText(name).split(/\s+/).filter(Boolean);
  if (!parts.length) return "DU";
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

const displayableLogoSrc = (value) => {
  const logo = cleanText(value);
  return /^(https?:|\/|data:image|blob:)/i.test(logo) ? logo : "";
};

export function Logo({ siteProfile = {} } = {}) {
  const logoSrc = displayableLogoSrc(siteProfile.logoUrl) || displayableLogoSrc(siteProfile.logoSignedUrl) || displayableLogoSrc(siteProfile.logo);
  const logoAlt = cleanText(siteProfile.name) || "Auto Portal";

  return (
    <Link className="brand" to="/">
      {logoSrc ? (
        <img className="brand-logo dealer-brand-logo" src={logoSrc} alt={logoAlt} />
      ) : (
        <span className="dealer-brand-text">{logoAlt}</span>
      )}
    </Link>
  );
}

export function Footer({ publicLinks = false, siteProfile = {} } = {}) {
  const { isAuthenticated } = useAuth();
  const year = new Date().getFullYear();
  const footerBrandName = cleanText(siteProfile.name) || brandName;
  const platformLinks = publicLinks || !isAuthenticated
    ? [
      ["Inventory", "#inventory"],
      ["Search", "#filters"],
      ["Sign In", "/login"],
    ]
    : [
      ["Inventory", "/home"],
      ["Vehicles", "/vehicles"],
      ["Appointments", "/schedules"],
      ["Search", "/dashboard"],
      ["Workspaces", "/workspaces"],
      ["Access Control", "/access-control"],
      ["Invitations", "/invitations"],
    ];

  return (
    <footer className="site-footer care-site-footer">
      <div>
        <Logo siteProfile={siteProfile} />
        <p>Browse available cars and search current inventory.</p>
        <small>© {year} {footerBrandName}. All rights reserved.</small>
      </div>
      <div>
        <strong>Platform</strong>
        {platformLinks.map(([label, to]) =>
          publicLinks && to.startsWith("#") ? <a href={to} key={label}>{label}</a> : <Link to={to} key={label}>{label}</Link>,
        )}
      </div>
      <div>
        <strong>Resources</strong>
        {publicLinks ? <a href="#inventory">Inventory</a> : <Link to="/support">Support</Link>}
        {publicLinks ? <a href="#filters">Search</a> : null}
        <a href="/privacy-policy">Privacy Policy</a>
      </div>
      <div>
        <strong>Follow Us</strong>
        <div className="socials">
          <span><i className="ti ti-brand-facebook" /></span>
          <span><i className="ti ti-brand-linkedin" /></span>
          <span><i className="ti ti-brand-instagram" /></span>
        </div>
      </div>
    </footer>
  );
}

export function HeaderUserProfile() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [ddOpen, setDdOpen] = useState(false);
  const menuRef = useRef(null);
  const name = userDisplayName(user);
  const role = userRoleLabel(user);
  const photo = cleanText(
    user?.profilePhoto || user?.avatarUrl || user?.photoUrl || user?.imageUrl,
  );
  const email = cleanText(user?.email) || "Dealer account";
  const initials = useMemo(() => userInitials(name), [name]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuRef.current || menuRef.current.contains(event.target)) return;
      setDdOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const profileMenuItems = [
    {
      label: "Profile",
      description: "View your account details",
      icon: <UserOutlined />,
      tone: "blue",
      path: paths.profile,
    },
    {
      label: "Settings",
      description: "Manage dealership site details",
      icon: <SettingOutlined />,
      tone: "yellow",
      path: "/site-settings",
    },
    {
      label: "Help",
      description: "Guides, answers, and tips",
      icon: <QuestionCircleOutlined />,
      tone: "blue",
      path: paths.help,
    },
    {
      label: "Support",
      description: "Get help with your dealership account",
      icon: <CustomerServiceOutlined />,
      tone: "orange",
      path: paths.support,
    },
  ];

  const goToProfileMenuItem = (path) => {
    setDdOpen(false);
    navigate(path);
  };

  return (
    <div className="profile-tools">
      <span className="notification">
        <i className="ti ti-bell" />
        <b>3</b>
      </span>
      <div
        className="care-profile-trigger pm-avw"
        ref={menuRef}
        role="button"
        tabIndex={0}
        aria-label="Open profile menu"
        aria-expanded={ddOpen}
        onClick={(event) => {
          event.stopPropagation();
          setDdOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setDdOpen((current) => !current);
        }}
      >
        <div className="pm-av care-profile-avatar">
          <Avatar
            src={photo || undefined}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "999px",
              background: photo
                ? undefined
                : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebDarkBlue}, ${OLIVEEB_COLORS.oliveebBlue})`,
              color: OLIVEEB_COLORS.white,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {!photo ? initials : null}
          </Avatar>
          <span className="pm-avon" />
        </div>
        <div className="profile-label">
          <strong>{name}</strong>
          <span>{role}</span>
        </div>
        <i
          className={`ti ti-chevron-down care-profile-chevron${ddOpen ? " is-open" : ""}`}
        />

        {ddOpen && (
          <div className="pm-dd" onClick={(event) => event.stopPropagation()}>
            <div className="pm-ddh">
              <div className="pm-dd-avatar">
                <Avatar
                  src={photo || undefined}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "999px",
                    background: photo
                      ? undefined
                      : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue})`,
                    color: OLIVEEB_COLORS.white,
                    fontSize: 16,
                    fontWeight: 900,
                  }}
                >
                  {!photo ? initials : null}
                </Avatar>
              </div>
              <div className="pm-dd-user">
                <div className="pm-dd-kicker">Signed in as</div>
                <div className="pm-ddn">{name}</div>
                <div className="pm-dde">{email}</div>
              </div>
            </div>
            <div className="pm-dd-list">
              {profileMenuItems.map((item) => (
                <div
                  key={item.label}
                  className="pm-ddi pm-dd-action"
                  data-tone={item.tone}
                  onClick={() => goToProfileMenuItem(item.path)}
                >
                  <span className="pm-dd-ico">{item.icon}</span>
                  <span className="pm-dd-copy">
                    <span className="pm-dd-label">{item.label}</span>
                    <span className="pm-dd-desc">{item.description}</span>
                  </span>
                </div>
              ))}
            </div>
            <div
              className="pm-ddi pm-dd-action dng mt-2"
              onClick={() => logout(false, paths.landing)}
            >
              <span className="pm-dd-ico">
                <LogoutOutlined />
              </span>
              <span className="pm-dd-copy">
                <span className="pm-dd-label">Sign out</span>
                <span className="pm-dd-desc">End this browser session</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SideNav({ collapsed = false }) {
  const [openGroups, setOpenGroups] = useState({});
  const [workspaceRolesReady, setWorkspaceRolesReady] = useState(false);
  const location = useLocation();
  const { user, careRoles = [], refreshCareRoles } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, isOrganizationWorkspace } = useWorkspace();
  const workspaceId = selectedWorkspaceId || "current";
  const workspaceName = workspaceDisplayName(selectedWorkspace);
  const workspaceType = workspaceDisplayType(selectedWorkspace);
  const actorId = currentUserId(user);
  const normalizedCareRoles = careRoles.map(normalizeRoleToken);
  const canManageCareAdminFeatures = workspaceRolesReady && (
    normalizedCareRoles.includes("care_owner") ||
    normalizedCareRoles.includes("care_admin") ||
    normalizedCareRoles.includes("auto_owner") ||
    normalizedCareRoles.includes("auto_admin")
  );

  const ownerType = websiteOwnerTypeForWorkspace({
    isOrganizationWorkspace,
    selectedWorkspace,
  });

  const organizationSettingsPath = selectedWorkspaceId
    ? paths.workspaceOrganization(selectedWorkspaceId)
    : paths.organizations;

  useEffect(() => {
    let active = true;
    setWorkspaceRolesReady(false);
    if (!actorId || !refreshCareRoles || !selectedWorkspaceId) {
      return () => {
        active = false;
      };
    }
    refreshCareRoles({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId }).finally(() => {
      if (active) setWorkspaceRolesReady(true);
    });
    return () => {
      active = false;
    };
  }, [actorId, refreshCareRoles, selectedWorkspaceId]);

  const careMenuItems = [
    ["Home", "ti-home", "/home"],
  ];
  const vehicleCareMenuItems = [
    ["Vehicles", "ti-car", "/vehicles"],
    ["Appointments", "ti-calendar-clock", "/schedules"],
    ["Customer Inquiries", "ti-message-circle", "/customer-inquiries"],
    ["Reports", "ti-chart-line", "/reports"],
    ["Appraisals", "ti-clipboard-text", "/appraisals"],
    ["Merchandising", "ti-speakerphone", "/merchandising"],
  ].filter(Boolean);
  const downloadsMenuItem = ["Documents", "ti-files", "/downloads"];
  const profileMenuItems = [
    ["Account", "ti-user-circle", paths.profile],
  ].filter(Boolean);
  const workspaceAdminMenuItems = [
    ["Workspaces", "ti-layout-grid", "/workspaces"],
    ["Dealership", "ti-building-store", organizationSettingsPath],
    ["Invitations", "ti-mail-opened", "/invitations"],
    ...(
      ownerType === "PORTFOLIO" || !canManageCareAdminFeatures
        ? []
        : [["Website", "ti-world", "/website"]]
    ),
    ...(canManageCareAdminFeatures ? [["Security & Access", "ti-shield-lock", "/access-control"]] : []),
  ];
  const communicationSupportMenuItems = [
    ["Settings", "ti-settings", "/site-settings"],
    ["Chat", "ti-messages", "/chat-space"],
    ["Inbox", "ti-inbox", "/email-messages"],
    ["Help & Support", "ti-headset", "/support"],
    ["Share Feedback", "ti-message-report", "/feedback"],
    ["Legal and Privacy", "ti-file-certificate", "/privacy-policy"],
  ];
  const paymentPlanMenuItems = [
    ["Wallet", "ti-wallet", `/workspaces/${workspaceId}/payment-methods`],
    ["Charges", "ti-receipt", `/workspaces/${workspaceId}/charges`],
    ["Subscriptions", "ti-repeat", `/workspaces/${workspaceId}/subscriptions`],
    ["Partnerships", "ti-heart-handshake", "/partners"],
  ];
  const isExternalPath = (path) => String(path || "").startsWith("http");
  const isActivePath = (path) =>
    !isExternalPath(path) &&
    (location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`)));
  const isGroupActive = (items = []) => items.some((item) => isActivePath(item[2]));
  const toggleGroup = (key) => {
    setOpenGroups((current) => ({ ...current, [key]: !current[key] }));
  };
  const renderSectionLabel = (label) => (
    <div className="sidebar-section-label" key={`section-${label}`}>
      {label}
    </div>
  );
  const renderMenuItem = ([label, icon, path]) => {
    const external = isExternalPath(path);
    const content = (
      <>
      <i className={`ti ${icon}`} />
      <span className="sidebar-label">{label}</span>
      </>
    );
    return external ? (
      <a className="" href={path} key={label} title={label} target="_blank" rel="noreferrer">
        {content}
      </a>
    ) : (
      <Link className={isActivePath(path) ? "active" : ""} to={path} key={label} title={label}>
        {content}
      </Link>
    );
  };
  const renderSubMenuItem = ([label, icon, path]) => {
    const external = isExternalPath(path);
    const content = (
      <>
      <i className={`ti ${icon}`} />
      <span>{label}</span>
      </>
    );
    return external ? (
      <a className="sidebar-subitem" href={path} key={label} title={label} target="_blank" rel="noreferrer">
        {content}
      </a>
    ) : (
      <Link className={`sidebar-subitem${isActivePath(path) ? " active" : ""}`} to={path} key={label} title={label}>
        {content}
      </Link>
    );
  };
  const renderMenuGroup = ({ key, label, icon, items, activeOverride = false, children }) => {
    if (!items?.length && !children) return null;
    const active = activeOverride || isGroupActive(items);
    const isOpen = Boolean(openGroups[key]) || active;
    return (
      <div className="sidebar-menu-group" key={key}>
        <button
          type="button"
          className={`sidebar-group-toggle${isOpen ? " is-open" : ""}${active ? " active" : ""}`}
          onClick={() => toggleGroup(key)}
          title={label}
          aria-expanded={isOpen}
        >
          <i className={`ti ${icon}`} />
          <span className="sidebar-label">{label}</span>
          <i className="ti ti-chevron-down sidebar-chevron" />
        </button>
        {isOpen && !collapsed && (
          <div className="sidebar-submenu">
            {items?.map(renderSubMenuItem)}
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={`sidebar${collapsed ? " is-collapsed" : ""}`}>
      <Logo />
      <nav>
        {renderSectionLabel("Auto")}
        {careMenuItems.map(renderMenuItem)}
        {isOrganizationWorkspace ? renderMenuGroup({
          key: "vehicleCare",
          label: "Inventory",
          icon: "ti-car",
          items: vehicleCareMenuItems,
        }) : null}
        {downloadsMenuItem ? renderMenuItem(downloadsMenuItem) : null}

        {renderSectionLabel("Accounts")}
        {renderMenuGroup({
          key: "profiles",
          label: "Profiles",
          icon: "ti-id",
          items: profileMenuItems,
        })}
        {renderMenuGroup({
          key: "workspaceAdmin",
          label: "Workspaces",
          icon: "ti-building",
          items: workspaceAdminMenuItems,
        })}
        {renderSectionLabel("General")}
        {renderMenuGroup({
          key: "communicationSupport",
          label: "Communication & Support",
          icon: "ti-messages",
          items: communicationSupportMenuItems,
        })}

        {canManageCareAdminFeatures ? (
          <>
            {renderSectionLabel("Payments & Plans")}
            {renderMenuGroup({
              key: "paymentsPlans",
              label: "Payments & Plans",
              icon: "ti-wallet",
              items: paymentPlanMenuItems,
            })}
          </>
        ) : null}
      </nav>
      <Link className="facility-card" to={selectedWorkspaceId ? paths.workspaces : paths.pmDashboard}>
        <span className="soft-icon">
          <i className="ti ti-building-hospital" />
        </span>
        <div>
          <strong>{workspaceName}</strong>
          <small>{workspaceType}</small>
        </div>
        <i className="ti ti-chevron-down" />
      </Link>
      <div className="help-card">
        <strong>Need help?</strong>
        <p>Visit our Help Center or contact support.</p>
        <Link to={paths.help}>
          Help Center <i className="ti ti-arrow-right" />
        </Link>
      </div>
    </aside>
  );
}

export function CareShell({ children, className = "dashboard-content", embedded = false, siteProfile: providedSiteProfile = null }) {
  const { isAuthenticated } = useAuth();
  const { siteProfile: fetchedSiteProfile } = useSiteProfile({ enabled: !providedSiteProfile && !embedded });
  const siteProfile = providedSiteProfile || fetchedSiteProfile;

  if (embedded) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className="app-shell app-shell-no-sidebar">
      {!isAuthenticated ? <AutoContactStrip siteProfile={siteProfile} /> : null}
      <main className="dashboard-main">
        <div className="dashboard-top dealer-dashboard-top">
          <Logo siteProfile={siteProfile} />
          <nav className="dealer-header-nav" aria-label="Dealer navigation">
            {isAuthenticated ? (
              <>
                <Link to="/home">Home</Link>
                <Link to="/vehicles">Inventory</Link>
                <Link to="/schedules">Appointments</Link>
                <Link to="/customer-inquiries">Inquiries</Link>
                <Link to="/reports">Reports</Link>
                <Link to="/site-settings">Settings</Link>
                <a href="/#filters">Search</a>
                <a href="/#location">Location</a>
                <a href="/#contact">Contact Us</a>
              </>
            ) : (
              <>
                <a href="/#inventory">Inventory</a>
                <a href="/#filters">Search</a>
                <a href="/#location">Location</a>
                <a href="/#contact">Contact Us</a>
              </>
            )}
          </nav>
          {!isAuthenticated ? (
            <div className="dealer-header-actions">
              <a className="mini-btn dealer-call-link" href="tel:2533254800"><i className="ti ti-phone" /> (253) 325-4800</a>
              <a className="mini-btn dealer-whatsapp-link" href="https://wa.me/12533254800"><i className="ti ti-brand-whatsapp" /> WhatsApp</a>
            </div>
          ) : null}
          {isAuthenticated ? <HeaderUserProfile /> : null}
        </div>
        <div className={className}>
          {children}
          <Footer siteProfile={siteProfile} />
        </div>
      </main>
    </div>
  );
}

export function Panel({ title, action, actionNode, children }) {
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{title}</h2>
        {actionNode}
        {action && <a href="#view">{action}</a>}
      </div>
      {children}
    </section>
  );
}
